import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DataGridPremium, GridColDef } from "@mui/x-data-grid-premium";
import { Select, Option, Modal, ModalDialog } from "@mui/joy";
import { Search, Add, FileDownloadOutlined, UploadFileOutlined } from "@mui/icons-material";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getFetch, postFetch } from "../../../../fetch/fetch";
import { tableUseStyle } from "../../controls/etc/tableusestyle";
import styles from "./faqadmin.module.css";
import { formatMonthDay, formatNumber, downloadBase64Xlsx, toastOptions } from "./faqutil";
import FaqImportModal from "./faqimportmodal";

/**
 * FAQ 관리 > ① FAQ 관리 소탭 — 편집용 목록 (faq_item). 서버 GET /api/faq/items (서버 페이지네이션)
 *  - 검색(질문/답변/세부분류/#id LIKE) · 카테고리 · 상태(활성/삭제됨) 필터
 *  - 행 클릭 또는 [수정] → 상위(faqadminmain) 드로어. [삭제] 는 soft delete, 삭제됨 필터에서 [복구]
 *  - 미배포 배지 = updated_at 이 최신 배포 이후
 *  - 엑셀로 한번에 추가(3단계 모달) / 엑셀 내보내기(active 전체 xlsx)
 */
const FaqItems : React.FC<any> = (props : any) => {

    const classes = tableUseStyle();
    const [q, setQ] = useState("");
    const [qInput, setQInput] = useState("");
    const [category, setCategory] = useState<string>("");
    const [status, setStatus] = useState<"active" | "deleted">("active");
    const [paginationModel, setPaginationModel] = useState({ page : 0, pageSize : 50 });
    const [rows, setRows] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [counts, setCounts] = useState<any>({ active : 0, deleted : 0 });
    const [loading, setLoading] = useState(false);
    const [importOpen, setImportOpen] = useState(false);
    const [confirm, setConfirm] = useState<{ open : boolean, row? : any, action? : "delete" | "restore" }>({ open : false });
    const [exporting, setExporting] = useState(false);
    const debounceRef = useRef<any>(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (q) params.set("q", q);
            if (category) params.set("category", category);
            params.set("status", status);
            params.set("page", String(paginationModel.page));
            params.set("size", String(paginationModel.pageSize));
            const result : any = await getFetch(`/api/faq/items?${params.toString()}`);
            if (result && result.message === "success") {
                setRows(result.items);
                setTotal(+result.total);
                setCounts(result.counts);
            }
        } catch (e) {
            console.log(e);
        } finally {
            setLoading(false);
        }
    }, [q, category, status, paginationModel]);

    useEffect(() => {
        load();
    }, [load, props.refreshKey]);

    // 검색어 디바운스 300ms
    const onSearchChange = (v : string) => {
        setQInput(v);
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => {
            setQ(v.trim());
            setPaginationModel((p) => ({ ...p, page : 0 }));
        }, 300);
    };

    const doAction = async () => {
        if (!confirm.row || !confirm.action) {
            return;
        }
        try {
            const result : any = await postFetch(`/api/faq/items/${confirm.row.id}/${confirm.action}`, {});
            if (result && result.message === "success") {
                toast.success(confirm.action === "delete" ? `#${confirm.row.id} 삭제됨 (배포 전까지 직원 화면에는 그대로 나갑니다)` : `#${confirm.row.id} 복구됨`, toastOptions);
                setConfirm({ open : false });
                props.onChanged();
            } else {
                toast.error("처리에 실패했습니다", toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("처리에 실패했습니다", toastOptions);
        }
    };

    const exportXlsx = async () => {
        setExporting(true);
        try {
            const result : any = await getFetch("/api/faq/items/export");
            if (result && result.message === "success") {
                downloadBase64Xlsx(result.fileName, result.base64);
            } else {
                toast.error("내보내기에 실패했습니다", toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("내보내기에 실패했습니다", toastOptions);
        } finally {
            setExporting(false);
        }
    };

    const columns : GridColDef[] = useMemo(() => [
        { field : "id", headerName : "#", width : 64, renderCell : (p : any) => <span className={styles.cellMuted}>{p.value}</span> },
        { field : "category", headerName : "카테고리", width : 130 },
        { field : "subcategory", headerName : "세부분류", width : 170, renderCell : (p : any) => <span className={styles.cellSub}>{p.value}</span> },
        { field : "question", headerName : "질문", flex : 1, minWidth : 240, renderCell : (p : any) => <div className={styles.cellQ}>{p.value}</div> },
        { field : "answer", headerName : "답변", width : 320, renderCell : (p : any) => <div className={styles.clamp2}>{p.value}</div> },
        { field : "updated_at", headerName : "수정일", width : 90, renderCell : (p : any) => <span className={styles.cellMuted}>{formatMonthDay(p.value)}</span> },
        { field : "unreleased", headerName : "", width : 84, sortable : false, renderCell : (p : any) => (p.value ? <span className={`${styles.badge} ${styles.badgeAmber} ${styles.badgeSm}`}>미배포</span> : null) },
        { field : "feedbackCount", headerName : "피드백", width : 72, renderCell : (p : any) => (p.value > 0 ? <span className={`${styles.badge} ${styles.badgeRed} ${styles.badgeSm}`}>{p.value}</span> : null) },
        {
            field : "actions", headerName : "", width : 130, sortable : false, filterable : false,
            renderCell : (p : any) => (
                <div className={styles.rowFlex} onClick={(e) => e.stopPropagation()}>
                    {
                        status === "active" ?
                            <>
                                <button className={`${styles.btn} ${styles.btnSm}`} onClick={() => props.onEdit(p.row.id)}>수정</button>
                                <button className={`${styles.btn} ${styles.btnSm} ${styles.btnDanger}`} onClick={() => setConfirm({ open : true, row : p.row, action : "delete" })}>삭제</button>
                            </>
                            :
                            <button className={`${styles.btn} ${styles.btnSm} ${styles.btnOutline}`} onClick={() => setConfirm({ open : true, row : p.row, action : "restore" })}>복구</button>
                    }
                </div>
            )
        }
    ], [status, props.onEdit]);

    return (
        <div className={styles.section}>
            <div className={styles.toolbar}>
                <div className={styles.searchBox}>
                    <Search sx={{ fontSize : 18, color : "#9CA3AF" }} />
                    <input value={qInput} onChange={(e) => onSearchChange(e.target.value)} placeholder="질문·답변·세부분류·번호 검색" aria-label="검색" />
                </div>
                <Select size="md" value={category} onChange={(e, v) => { setCategory(v === null ? "" : String(v)); setPaginationModel((p) => ({ ...p, page : 0 })); }} sx={{ minWidth : "170px", borderRadius : "10px", height : "44px" }}>
                    <Option value="">전체 카테고리</Option>
                    {props.categories.map((c : any) => <Option key={c.id} value={c.name}>{c.name} ({c.count})</Option>)}
                </Select>
                <button className={`${styles.pill} ${status === "active" ? styles.pillActive : ""}`} onClick={() => { setStatus("active"); setPaginationModel((p) => ({ ...p, page : 0 })); }}>활성 {formatNumber(counts.active)}</button>
                <button className={`${styles.pill} ${status === "deleted" ? styles.pillActive : ""}`} onClick={() => { setStatus("deleted"); setPaginationModel((p) => ({ ...p, page : 0 })); }}>삭제됨 {formatNumber(counts.deleted)}</button>
                <div style={{ marginLeft : "auto", display : "flex", gap : "8px", flexWrap : "wrap" }}>
                    <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => props.onNew()}><Add sx={{ fontSize : 18 }} /> 새 FAQ</button>
                    <button className={`${styles.btn} ${styles.btnOutline}`} onClick={() => setImportOpen(true)}><UploadFileOutlined sx={{ fontSize : 18 }} /> 엑셀로 한번에 추가</button>
                    <button className={styles.btn} onClick={exportXlsx} disabled={exporting}><FileDownloadOutlined sx={{ fontSize : 18 }} /> 엑셀 내보내기</button>
                </div>
            </div>

            <div className={`${classes.root} ${styles.gridWrap}`} style={{ height : "620px" }}>
                <DataGridPremium
                    rows={rows}
                    columns={columns}
                    density="compact"
                    loading={loading}
                    disableRowSelectionOnClick
                    getRowHeight={() => "auto"}
                    onRowClick={(params) => { if (status === "active") props.onEdit(params.row.id); }}
                    pagination
                    paginationMode="server"
                    rowCount={total}
                    paginationModel={paginationModel}
                    onPaginationModelChange={(m) => setPaginationModel(m)}
                    pageSizeOptions={[20, 50, 100]}
                    sx={{
                        "& .MuiDataGrid-cell" : { padding : "8px 10px", alignItems : "flex-start" },
                        "& .MuiDataGrid-row:hover" : { cursor : status === "active" ? "pointer" : "default" }
                    }}
                />
            </div>
            <div className={styles.footerRow}>
                <span>{q || category ? `검색 결과 ${formatNumber(total)}건` : `${status === "active" ? "활성" : "삭제됨"} ${formatNumber(total)}건`}</span>
                <span>행을 클릭하면 오른쪽 드로어에서 수정 · 저장해도 배포 전까지는 직원 화면에 반영되지 않습니다</span>
            </div>

            <FaqImportModal open={importOpen} onClose={() => setImportOpen(false)} onCommitted={() => { setImportOpen(false); props.onChanged(); }} />

            <Modal open={confirm.open} onClose={() => setConfirm({ open : false })}>
                <ModalDialog sx={{ padding : 0, borderRadius : "14px", width : "400px", maxWidth : "95vw" }}>
                    <div className={styles.modal} style={{ boxShadow : "none" }}>
                        <div className={styles.modalHeader}>{confirm.action === "delete" ? "FAQ 삭제" : "FAQ 복구"}</div>
                        <div className={styles.modalBody}>
                            {
                                confirm.row ?
                                    <div style={{ fontSize : "14px", lineHeight : 1.6 }}>
                                        <b>#{confirm.row.id}</b> {confirm.row.question}
                                        <div className={styles.helper} style={{ marginTop : "8px" }}>
                                            {confirm.action === "delete" ? "삭제됨 상태로 바뀌고(복구 가능), 다음 배포부터 직원 화면에서 빠집니다." : "활성으로 되돌리고, 다음 배포부터 직원 화면에 다시 나갑니다."}
                                        </div>
                                    </div>
                                    : null
                            }
                        </div>
                        <div className={styles.modalFooter}>
                            <button className={styles.btn} onClick={() => setConfirm({ open : false })}>취소</button>
                            <button className={`${styles.btn} ${confirm.action === "delete" ? styles.btnDanger : styles.btnPrimary}`} onClick={doAction}>{confirm.action === "delete" ? "삭제" : "복구"}</button>
                        </div>
                    </div>
                </ModalDialog>
            </Modal>
        </div>
    );
};

export default FaqItems;
